import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import type {} from '@redux-devtools/extension' // required for devtools typing
import {
  listExportTemplates,
  requestExportTemplateUploadUrl,
  createExportTemplate,
  renameExportTemplate,
  deleteExportTemplate,
  setDefaultExportTemplate,
  getExportTemplateDownloadUrl,
  uploadFileToS3,
} from '@/lib/api'
import type { ExportTemplate } from '@/lib/api'
import { templateIssueFromError, type TemplateIssue } from '@/lib/template-check'

// ===================
// Types
// ===================

interface ExportTemplatesState {
  // State
  templates: ExportTemplate[]
  isLoading: boolean
  error: string | null
  lastFetchedAt: number | null
  /**
   * A template issue an export was refused for (D4): shown by the export
   * dropdown, which offers to continue. Null when there is none.
   */
  templateIssue: TemplateIssue | null
  /** Runs the refused export again, acknowledged. Absent for a check-only issue. */
  templateIssueRetry: (() => void) | null
  /** Set by the export warning's "Fix / Add the placeholder": the templates page shows this how-to. */
  fixGuide: { templateName: string; howTo: string } | null

  // Actions
  fetchTemplates: (force?: boolean) => Promise<void>
  /** Resolves with the created template, including its check report. */
  uploadTemplate: (name: string, file: File) => Promise<ExportTemplate>
  rename: (id: string, name: string) => Promise<void>
  remove: (id: string) => Promise<void>
  setDefault: (id: string) => Promise<void>
  downloadTemplate: (id: string) => Promise<void>
  showTemplateIssue: (issue: TemplateIssue, retry?: () => void) => void
  clearTemplateIssue: () => void
  setFixGuide: (guide: { templateName: string; howTo: string } | null) => void
  clearError: () => void
  reset: () => void
}

// ===================
// Constants
// ===================

const STALE_THRESHOLD_MS = 5 * 60 * 1000 // 5 minutes

// ===================
// Store
// ===================

export const useExportTemplatesStore = create<ExportTemplatesState>()(
  devtools(
    (set, get) => ({
      templates: [],
      isLoading: false,
      error: null,
      lastFetchedAt: null,
      templateIssue: null,
      templateIssueRetry: null,
      fixGuide: null,

      fetchTemplates: async (force = false) => {
        const { lastFetchedAt, isLoading } = get()
        if (isLoading) return

        // Skip if data is fresh (< 5 minutes old) unless forced
        if (!force && lastFetchedAt && Date.now() - lastFetchedAt < STALE_THRESHOLD_MS) {
          return
        }

        set({ isLoading: true, error: null })
        try {
          const templates = await listExportTemplates()
          set({ templates, lastFetchedAt: Date.now() })
        } catch (err) {
          set({ error: err instanceof Error ? err.message : 'Failed to load templates' })
        } finally {
          set({ isLoading: false })
        }
      },

      uploadTemplate: async (name: string, file: File) => {
        set({ error: null })
        try {
          // 1. Get presigned upload URL
          const { uploadUrl, templateId } = await requestExportTemplateUploadUrl(
            file.name,
            file.size,
          )

          // 2. Upload file to S3
          await uploadFileToS3(uploadUrl, file)

          // 3. Create template record
          const template = await createExportTemplate({
            templateId,
            name,
            originalFilename: file.name,
            fileSizeBytes: file.size,
          })

          // 4. Add to local state
          set((state) => ({
            templates: [...state.templates, template],
            lastFetchedAt: Date.now(),
          }))
          return template
        } catch (err) {
          const message = err instanceof Error ? err.message : 'Upload failed'
          set({ error: message })
          throw err
        }
      },

      rename: async (id: string, name: string) => {
        set({ error: null })
        try {
          const updated = await renameExportTemplate(id, name)
          set((state) => ({
            templates: state.templates.map((t) => (t.id === id ? updated : t)),
          }))
        } catch (err) {
          const message = err instanceof Error ? err.message : 'Rename failed'
          set({ error: message })
          throw err
        }
      },

      remove: async (id: string) => {
        set({ error: null })
        try {
          await deleteExportTemplate(id)
          set((state) => ({
            templates: state.templates.filter((t) => t.id !== id),
          }))
        } catch (err) {
          const message = err instanceof Error ? err.message : 'Delete failed'
          set({ error: message })
          throw err
        }
      },

      setDefault: async (id: string) => {
        set({ error: null })
        try {
          const updated = await setDefaultExportTemplate(id)
          set((state) => ({
            templates: state.templates.map((t) => {
              if (t.id === id) return updated
              if (t.isDefault) return { ...t, isDefault: false }
              return t
            }),
          }))
        } catch (err) {
          const message = err instanceof Error ? err.message : 'Failed to set default'
          set({ error: message })
          throw err
        }
      },

      downloadTemplate: async (id: string) => {
        try {
          const { downloadUrl, filename } = await getExportTemplateDownloadUrl(id)
          const a = document.createElement('a')
          a.href = downloadUrl
          a.download = filename
          document.body.appendChild(a)
          a.click()
          document.body.removeChild(a)
        } catch (err) {
          const message = err instanceof Error ? err.message : 'Download failed'
          set({ error: message })
          throw err
        }
      },

      // The refused export's report also becomes the template's cached report,
      // so the next export from this template warns before it asks the server.
      showTemplateIssue: (issue: TemplateIssue, retry?: () => void) =>
        set((state) => ({
          templateIssue: issue,
          templateIssueRetry: retry ?? null,
          templates: state.templates.map((t) =>
            t.id === issue.templateId ? { ...t, checkReport: issue.report } : t,
          ),
        })),

      clearTemplateIssue: () => set({ templateIssue: null, templateIssueRetry: null }),

      setFixGuide: (fixGuide) => set({ fixGuide }),

      clearError: () => set({ error: null }),

      reset: () =>
        set({
          templates: [],
          isLoading: false,
          error: null,
          lastFetchedAt: null,
          templateIssue: null,
          templateIssueRetry: null,
          fixGuide: null,
        }),
    }),
    { name: 'export-templates-store' },
  ),
)

/**
 * Hand a refused export (409 TEMPLATE_CONTENT_LOSS, D4) to the template
 * warning (`TemplateIssueHost`), which offers "Export anyway" — that runs
 * `retry`, which must resend the same export with `acknowledgeTemplateLoss`.
 * Returns true when `err` was a template refusal (the caller then skips its
 * error toast); false for every other error.
 */
export function routeTemplateExportError(err: unknown, retry: () => void): boolean {
  const issue = templateIssueFromError(err)
  if (!issue) return false
  useExportTemplatesStore.getState().showTemplateIssue(issue, retry)
  return true
}
